/**
 * Customer-portal i18n (OCP-656) - German and English, the same i18next /
 * react-i18next pair core's product UI uses (`apps/web/src/i18n`).
 *
 * Catalogues: `locales/<lang>/<namespace>.json`, one namespace per page or
 * component family (`common` for the shell and shared vocabulary). Every
 * namespace is loaded eagerly - the portal is small and the customer switches
 * languages without a reload. `locale-coverage.test.ts` fails when `de` and
 * `en` do not carry the identical key set.
 *
 * Resolution order for the initial language: the signed-in user's stored
 * choice (`/v1/portal/auth/me` -> `locale`, applied by the auth context) >
 * the browser-local choice (`localStorage`) > `navigator.language` > `en`.
 * `setLocale()` applies + persists locally; the auth context also PATCHes
 * `/v1/portal/me/locale` so the choice follows the user across devices and
 * drives the mails the portal triggers.
 *
 * Contract wording rule: labels the AGB name ("Kostenpflichtig Buchen",
 * "Abonnement Kündigen", "Backups") are catalogue keys whose German value is
 * fixed by the published terms (OWS-D23), not free translations. The section
 * name "Subscriptions" stays identical in both languages (operator decision
 * 2026-09-10).
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

export const LOCALE_LABELS: Record<string, string> = {
  en: 'English',
  de: 'Deutsch',
};
export const SUPPORTED_LOCALES = Object.keys(LOCALE_LABELS) as readonly string[];
export type PortalLocale = 'de' | 'en';
export const DEFAULT_LOCALE: PortalLocale = 'en';
export const LOCALE_STORAGE_KEY = 'orboto_portal_locale';

export function isPortalLocale(v: unknown): v is PortalLocale {
  return v === 'de' || v === 'en';
}

// `import.meta.glob` keeps the catalogues as separate JSON modules so a
// namespace is one file per language - the shape the coverage test compares.
const modules = import.meta.glob('./locales/*/*.json', { eager: true }) as Record<string, { default: Record<string, unknown> }>;

const resources: Record<string, Record<string, Record<string, unknown>>> = {};
const namespaces = new Set<string>();
for (const [path, mod] of Object.entries(modules)) {
  const m = /\/locales\/([a-z]{2})\/([a-z0-9-]+)\.json$/.exec(path);
  if (!m) continue;
  const [, lang, ns] = m;
  resources[lang!] ??= {};
  resources[lang!]![ns!] = mod.default;
  namespaces.add(ns!);
}
export const NAMESPACES = [...namespaces].sort();

function readStoredLocale(): PortalLocale | null {
  try {
    const v = localStorage.getItem(LOCALE_STORAGE_KEY);
    return isPortalLocale(v) ? v : null;
  } catch {
    return null;
  }
}

export function detectInitialLocale(): PortalLocale {
  const stored = readStoredLocale();
  if (stored) return stored;
  const browser = typeof navigator !== 'undefined' ? navigator.language.slice(0, 2).toLowerCase() : '';
  return isPortalLocale(browser) ? browser : DEFAULT_LOCALE;
}

void i18n.use(initReactI18next).init({
  lng: detectInitialLocale(),
  fallbackLng: DEFAULT_LOCALE,
  supportedLngs: SUPPORTED_LOCALES as string[],
  ns: NAMESPACES,
  defaultNS: 'common',
  resources,
  interpolation: { escapeValue: false },
  returnNull: false,
});

/** The active language as a portal locale (never a region variant). */
export function currentLocale(): PortalLocale {
  const l = (i18n.resolvedLanguage ?? i18n.language ?? DEFAULT_LOCALE).slice(0, 2);
  return isPortalLocale(l) ? l : DEFAULT_LOCALE;
}

/** BCP-47 tag for `Intl` formatting of dates, numbers and money. */
export function intlTag(locale: PortalLocale = currentLocale()): string {
  return locale === 'de' ? 'de-DE' : 'en-GB';
}

/** Apply a language locally (i18next + `<html lang>` + localStorage). */
export async function applyLocale(locale: PortalLocale): Promise<void> {
  if (currentLocale() !== locale) await i18n.changeLanguage(locale);
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // private mode / blocked storage - the in-memory language still switched
  }
}

export default i18n;
